import { useEffect, useState } from 'react';
import type { AgentVersionDTO } from '../../types';
import { useAgentVersionAdminApiClient } from '../../context/AiAgentAdminApiContext';
import { UnverifiedBanner } from '../Shared/UnverifiedBanner';

/**
 * Restored Versions tab (Phase 3 of the Agent-editor sub-entity restoration — see
 * TemplatesPanel.tsx/RulesPanel.tsx/HooksPanel.tsx for this package's general panel shape).
 * Stays read-only exactly as designed — no create/update/delete UI, matching the old dialog's own
 * deliberate design and this phase's own instruction. Real entity (AIAgent_Versions) confirmed
 * richer than briefed by reading Version.cs directly: ConfigSnapshot/InstructionSnapshot (JSON)/
 * IsActive/DeployedOn/DeployedBy/UndeployedOn/PerformanceMetrics (JSON), description field is
 * `Description` not `VersionDescription` — all as briefed. `VersionNumber` (required, string,
 * 50) is a real column the task briefing never mentioned — see types.ts's AgentVersionDTO
 * comment. Each row expands inline (no modal) to show the two JSON snapshots and performance
 * metrics, since there's no edit affordance to reuse a form-mode toggle for.
 */
export function VersionsPanel({ agentId }: { agentId: number }) {
  const api = useAgentVersionAdminApiClient();
  const [versions, setVersions] = useState<AgentVersionDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.getVersionsByAgent(agentId).then((result) => {
      if (cancelled) return;
      setVersions(result.data ?? []);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId]);

  return (
    <div>
      <UnverifiedBanner entityName="Versions" />
      {loading && <div className="aaa-editor-loading">Loading…</div>}
      {!loading && versions.length === 0 && <div className="aaa-entity-dropdown-empty">No versions yet.</div>}
      <div className="aaa-entity-grid">
        {versions.map((version) => {
          const expanded = expandedId === version.VersionID;
          return (
            <div key={version.VersionID} className="aaa-entity-card">
              <div className="aaa-entity-card-header">
                <strong>v{version.VersionNumber}</strong>
                <span className={`aaa-badge ${version.IsActive ? 'aaa-badge-active' : 'aaa-badge-inactive'}`}>
                  {version.IsActive ? 'Deployed' : 'Not Deployed'}
                </span>
              </div>
              {version.Description && <p>{version.Description}</p>}
              <p>
                {version.DeployedOn ? `Deployed ${version.DeployedOn}` : 'Never deployed'}
                {version.UndeployedOn ? ` · Undeployed ${version.UndeployedOn}` : ''}
              </p>
              <div className="aaa-entity-card-actions">
                <button
                  type="button"
                  className="aaa-btn aaa-btn-default aaa-btn-sm"
                  onClick={() => setExpandedId(expanded ? null : version.VersionID)}
                >
                  {expanded ? 'Hide Details' : 'Show Details'}
                </button>
              </div>
              {expanded && (
                <div className="aaa-form-grid" style={{ marginTop: 8 }}>
                  <div className="aaa-form-group aaa-form-group-full">
                    <label>Config Snapshot</label>
                    <textarea rows={5} readOnly value={version.ConfigSnapshot ?? ''} />
                  </div>
                  <div className="aaa-form-group aaa-form-group-full">
                    <label>Instruction Snapshot</label>
                    <textarea rows={5} readOnly value={version.InstructionSnapshot ?? ''} />
                  </div>
                  <div className="aaa-form-group aaa-form-group-full">
                    <label>Performance Metrics</label>
                    <textarea rows={4} readOnly value={version.PerformanceMetrics ?? ''} />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
